import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useField } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { getIsNew } from '../../../store/order/selectors';
import { useToggleable } from '../../../../hooks';
export const usePassengerConsents = ({ showSubscribedCheckbox } = {}) => {
    const { t, i18n } = useTranslation('PassengersConsents');
    const isNew = useSelector(getIsNew);
    const checkboxRef = useRef(null);
    const [selectedKeys, setSelectedKeys] = useState([]);
    const termsField = useField('terms', {
        type: 'checkbox',
        initialValue: false,
        validate: value => {
            if (!value) {
                return t('Consents_ValidationMessage');
            }
        }
    });
    const subscribedField = useField('subscribed', {
        type: 'checkbox',
        initialValue: false
    });
    useEffect(() => {
        if (showSubscribedCheckbox) {
            subscribedField.input.onChange(selectedKeys.includes('Consents_Subscribed'));
        }
    }, [selectedKeys]);
    const { isOpen: isOpenTermsOfTariff, open: onOpenTermsOfTariff, close: onCloseTermsOfTariff } = useToggleable(false);
    const isExistTranslation = useCallback((key) => !!t(key) && i18n.exists(key, { ns: 'PassengersConsents' }), [i18n]);
    const conditionKeys = useMemo(() => {
        const conditions = [];
        if (isExistTranslation('Consents_TermsOfTariff')) {
            conditions.push({ key: 'Consents_TermsOfTariff', required: true });
        }
        if (isExistTranslation('Consents_TermsOfAgreement')) {
            conditions.push({ key: 'Consents_TermsOfAgreement', required: true });
        }
        if (isExistTranslation('Consents_Insurance')) {
            conditions.push({ key: 'Consents_Insurance', required: true });
        }
        if (isExistTranslation('Consents_PrivacyPolicy')) {
            conditions.push({ key: 'Consents_PrivacyPolicy', required: true });
        }
        if (showSubscribedCheckbox && isExistTranslation('Consents_Subscribed')) {
            conditions.push({ key: 'Consents_Subscribed' });
        }
        return conditions;
    }, [showSubscribedCheckbox, isExistTranslation]);
    const isVisible = isNew && !!conditionKeys.length;
    const isChecked = useMemo(() => {
        return conditionKeys.every(condition => selectedKeys.includes(condition.key));
    }, [selectedKeys, conditionKeys]);
    const isIndeterminate = useMemo(() => {
        return !isChecked && conditionKeys.some(condition => selectedKeys.includes(condition.key));
    }, [selectedKeys, conditionKeys, isChecked]);
    const isNeedConfirm = useMemo(() => {
        return isNew
            ? conditionKeys.some(condition => condition.required && !selectedKeys.includes(condition.key))
            : false;
    }, [selectedKeys, conditionKeys]);
    useEffect(() => {
        if (termsField.input.value !== !isNeedConfirm) {
            termsField.input.onChange(!isNeedConfirm);
            setTimeout(termsField.input.onBlur);
        }
    }, [isNeedConfirm]);
    const onToggleAll = useCallback(() => {
        setSelectedKeys(state => (!state.length ? conditionKeys.map(condition => condition.key) : []));
    }, [conditionKeys]);
    const onToggle = useCallback((key) => () => {
        setSelectedKeys(state => state.includes(key) ? state.filter(selectedKey => selectedKey !== key) : [...state, key]);
    }, [conditionKeys]);
    const onClick = useCallback((e) => {
        const target = e.target;
        const type = target.getAttribute('data-type');
        if (!type) {
            return;
        }
        e.preventDefault();
        if (type === 'termsOfTariff') {
            onOpenTermsOfTariff();
        }
    }, [onOpenTermsOfTariff]);
    return {
        // fields
        termsField,
        subscribedField,
        // data
        checkboxRef,
        selectedKeys,
        conditionKeys,
        isVisible,
        isChecked,
        isIndeterminate,
        isNeedConfirm,
        isOpenTermsOfTariff,
        // methods
        onToggleAll,
        onToggle,
        onClick,
        onOpenTermsOfTariff,
        onCloseTermsOfTariff
    };
};
